'use client'

import { createContext, useContext, useEffect, useMemo, useState } from "react"
import type { ReactNode } from "react"
import { getStoredLocale, setStoredLocale, type Locale, type Translation } from "@/utils/i18n"

type I18nContextValue = {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (value: Translation) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  // Keep first client render aligned with SSR ("es") to avoid hydration text mismatch.
  const [locale, setLocaleState] = useState<Locale>("es")

  useEffect(() => {
    const stored = getStoredLocale()
    setLocaleState(stored)
    // Sync HTML lang attribute with locale
    if (typeof document !== 'undefined') {
      document.documentElement.lang = stored
    }
  }, [])

  const setLocale = (next: Locale) => {
    setStoredLocale(next)
    setLocaleState(next)
    // Sync HTML lang attribute when locale changes
    if (typeof document !== 'undefined') {
      document.documentElement.lang = next
    }
  }

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (value: Translation) => value[locale] || value.en,
    }),
    [locale]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    throw new Error("useI18n must be used within I18nProvider")
  }
  return ctx
}
